import { PlusOutlined, UploadOutlined } from '@ant-design/icons'
import { Button, FormItem, Heading, Input, TextArea } from '@lmc/ui-kit'
import { Text } from '@lmc/ui-kit/src/general'
import {
  DatePicker as AntdDatePicker,
  Form as AntdForm,
  Divider,
  Modal,
  Upload,
  type UploadFile
} from 'antd'
import dayjs, { type Dayjs } from 'dayjs'
import utc from 'dayjs/plugin/utc'
import { NAMESPACE } from 'locale'
import { deviceRecordConstants } from 'locale/deviceRecords'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { MAX_DOCUMENTS } from '../constants'
import { useDeviceRecordCreate } from '../hooks/useDeviceRecordCreate'
import { useDeviceRecordUpdate } from '../hooks/useDeviceRecordUpdate'
import type {
  CreateDeviceRecordInput,
  DeviceRecord,
  UpdateDeviceRecordInput
} from '../types'

dayjs.extend(utc)

export type DeviceRecordModalProps = {
  isVisible: boolean
  onClose: () => void
  onSuccess: () => void
  deviceRecord?: DeviceRecord
  residentId: string
}

const DeviceRecordModal = ({
  isVisible,
  onClose,
  onSuccess,
  deviceRecord,
  residentId
}: DeviceRecordModalProps) => {
  const { t } = useTranslation(NAMESPACE.DEVICE_RECORDS)
  const [form] = AntdForm.useForm()
  const [fileList, setFileList] = useState<UploadFile[]>([])
  const isEditMode = !!deviceRecord
  const existingDocumentsCount = deviceRecord?.documents?.length || 0
  const remainingSlots = MAX_DOCUMENTS - existingDocumentsCount

  const { createDeviceRecord, isCreating } = useDeviceRecordCreate(() => {
    onSuccess()
  })

  const { updateDeviceRecord, isUpdating } = useDeviceRecordUpdate(
    residentId,
    () => {
      onSuccess()
    }
  )

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (isVisible) {
      if (deviceRecord) {
        form.setFieldsValue({
          name: deviceRecord.name || '',
          description: deviceRecord.description || '',
          location: deviceRecord.location || '',
          startDate: deviceRecord.startDate
            ? dayjs.utc(deviceRecord.startDate)
            : undefined,
          endDate: deviceRecord.endDate
            ? dayjs.utc(deviceRecord.endDate)
            : undefined
        })
        setFileList([])
      } else {
        form.resetFields()
        setFileList([])
      }
    }
  }, [isVisible, deviceRecord, form])

  const handleFileChange = (info: { fileList: UploadFile[] }) => {
    const maxAllowed = isEditMode ? remainingSlots : MAX_DOCUMENTS
    if (maxAllowed <= 0) {
      setFileList([])
      return
    }
    const limitedFileList = info.fileList.slice(-maxAllowed)
    setFileList(limitedFileList)
  }

  const handleSubmit = async (values: unknown) => {
    const formValues = values as {
      name: string
      description?: string
      location?: string
      startDate?: Dayjs | null
      endDate?: Dayjs | null
    }

    const toOptionalText = (value?: string): string | null | undefined => {
      if (value) return value
      return isEditMode ? null : undefined
    }

    const cleanedSubmitValues = Object.fromEntries(
      Object.entries({
        name: formValues.name,
        description: toOptionalText(formValues.description),
        location: toOptionalText(formValues.location),
        startDate: formValues.startDate
          ? formValues.startDate.toISOString()
          : isEditMode
            ? null
            : undefined,
        endDate: formValues.endDate
          ? formValues.endDate.toISOString()
          : isEditMode
            ? null
            : undefined
      } as CreateDeviceRecordInput).filter(([_, value]) => value !== undefined)
    ) as CreateDeviceRecordInput

    try {
      if (isEditMode && deviceRecord) {
        const existingDocumentIds = deviceRecord.documents
          ? deviceRecord.documents.map((doc) => doc.id).filter(Boolean)
          : []

        const updateInput: UpdateDeviceRecordInput = {
          id: deviceRecord.id,
          existingDocumentIds,
          ...cleanedSubmitValues
        }
        await updateDeviceRecord(updateInput, fileList)
      } else {
        await createDeviceRecord(cleanedSubmitValues, residentId, fileList)
      }
    } catch (error) {
      console.error('Failed to submit device record:', error)
    }
  }

  if (!isVisible) return null

  return (
    <Modal
      open={isVisible}
      onCancel={onClose}
      destroyOnClose
      footer={
        <ModalFooter>
          <Button onClick={onClose}>{t(deviceRecordConstants.CANCEL)}</Button>
          <Button
            onClick={form.submit}
            variant="primary"
            isLoading={isEditMode ? isUpdating : isCreating}
          >
            {isEditMode ? (
              t(deviceRecordConstants.SAVE)
            ) : (
              <>
                <PlusOutlined />
                {t(deviceRecordConstants.ADD)}
              </>
            )}
          </Button>
        </ModalFooter>
      }
      width={600}
    >
      <Heading level={3}>{t(deviceRecordConstants.DEVICE_RECORD)}</Heading>
      <Divider />
      <StyledForm
        form={form}
        onFinish={handleSubmit}
        layout="horizontal"
        key={deviceRecord?.id || 'new'}
      >
        <FormItem
          name="name"
          label={t(deviceRecordConstants.NAME)}
          rules={[
            {
              required: true,
              message: t(deviceRecordConstants.NAME_REQUIRED)
            }
          ]}
        >
          <Input
            maxLength={60}
            showCount
            placeholder={t(deviceRecordConstants.NAME_PLACEHOLDER)}
          />
        </FormItem>

        <FormItem name="location" label={t(deviceRecordConstants.LOCATION)}>
          <Input
            maxLength={120}
            showCount
            placeholder={t(deviceRecordConstants.LOCATION_PLACEHOLDER)}
          />
        </FormItem>

        <FormItem
          name="description"
          label={t(deviceRecordConstants.DESCRIPTION)}
        >
          <TextArea
            rows={4}
            maxLength={5000}
            placeholder={t(deviceRecordConstants.DESCRIPTION_PLACEHOLDER)}
          />
        </FormItem>

        <FormItem name="startDate" label={t(deviceRecordConstants.START_DATE)}>
          <AntdDatePicker
            format="DD-MM-YYYY"
            style={{ width: '100%' }}
            placeholder={t(deviceRecordConstants.START_DATE_PLACEHOLDER)}
          />
        </FormItem>

        <FormItem name="endDate" label={t(deviceRecordConstants.END_DATE)}>
          <AntdDatePicker
            format="DD-MM-YYYY"
            style={{ width: '100%' }}
            placeholder={t(deviceRecordConstants.END_DATE_PLACEHOLDER)}
          />
        </FormItem>

        <FormItem name="documents" label={t(deviceRecordConstants.DOCUMENTS)}>
          {isEditMode && existingDocumentsCount > 0 && (
            <InfoText>
              {t(deviceRecordConstants.EXISTING_DOCUMENTS_INFO, {
                count: existingDocumentsCount,
                remaining: remainingSlots
              })}
            </InfoText>
          )}
          <Upload
            fileList={fileList}
            beforeUpload={() => false}
            onChange={handleFileChange}
            accept=".pdf,.png,.jpg,.jpeg,.heic,.docm,.gif,.docx,.doc,.xlsx,.xls,.txt,.csv,.ppt,.pptx"
            maxCount={isEditMode ? remainingSlots : MAX_DOCUMENTS}
          >
            <Button
              disabled={
                isEditMode
                  ? fileList.length >= remainingSlots
                  : fileList.length >= MAX_DOCUMENTS
              }
            >
              <UploadOutlined />
              {t(deviceRecordConstants.UPLOAD_DOCUMENT)}
              {isEditMode
                ? fileList.length > 0 &&
                  ` (${fileList.length}/${remainingSlots})`
                : fileList.length > 0 &&
                  ` (${fileList.length}/${MAX_DOCUMENTS})`}
            </Button>
          </Upload>
          {((isEditMode && fileList.length >= remainingSlots) ||
            (!isEditMode && fileList.length >= MAX_DOCUMENTS)) && (
            <ErrorText>
              {isEditMode
                ? t(deviceRecordConstants.MAX_ADDITIONAL_DOCUMENTS_ALLOWED, {
                    count: remainingSlots
                  })
                : t(deviceRecordConstants.MAX_DOCUMENTS_ALLOWED, {
                    count: MAX_DOCUMENTS
                  })}
            </ErrorText>
          )}
        </FormItem>
        <Divider />
      </StyledForm>
    </Modal>
  )
}

const StyledForm = styled(AntdForm)`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const ModalFooter = styled.div`
  display: flex;
  gap: 16px;
  justify-content: flex-end;
`

const ErrorText = styled(Text)`
  color: #ff4d4f;
  font-size: 12px;
  margin-top: 4px;
  display: block;
`

const InfoText = styled(Text)`
  font-size: 12px;
  margin-bottom: 8px;
  display: block;
`

export default DeviceRecordModal
